import type { Ref } from "preact";
import { ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Single-line-growing chat composer. Forked from shadcn-chatbot-kit's
 * MessageInput, trimmed to the widget's needs (no file upload / audio) and
 * wired to controlled state. Enter submits; Shift+Enter inserts a newline.
 */
export function MessageInput({
  value,
  onChange,
  onSubmit,
  disabled = false,
  placeholder = "Type a message…",
  inputRef,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  placeholder?: string;
  /** Lets a parent focus the textarea (e.g. "Other" reason chip → open keyboard). */
  inputRef?: Ref<HTMLTextAreaElement>;
}) {
  const canSend = value.trim().length > 0 && !disabled;

  return (
    <div className="tw:flex tw:items-end tw:gap-2 tw:rounded-2xl tw:border tw:border-gray-200 tw:bg-white tw:px-3 tw:py-2 tw:focus-within:border-[#523D33]/60 tw:transition-colors">
      {/* text-base (16px) on mobile: iOS Safari auto-zooms the page when a
          focused input's computed font-size is under 16px. Desktop drops back
          to the compact size. */}
      <textarea
        ref={inputRef}
        value={value}
        rows={1}
        placeholder={placeholder}
        disabled={disabled}
        onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (canSend) onSubmit();
          }
        }}
        className="tw:max-h-28 tw:min-h-6 tw:flex-1 tw:resize-none tw:bg-transparent tw:text-base tw:md:text-sm tw:leading-snug tw:outline-none tw:text-[#523D33] tw:disabled:opacity-60"
        style={{ color: "var(--so-text, #523D33)" }}
      />
      <button
        type="button"
        aria-label="Send"
        disabled={!canSend}
        onClick={() => canSend && onSubmit()}
        className={cn(
          "tw:flex tw:size-8 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:transition-colors",
          canSend
            ? "tw:bg-[#523D33] tw:text-white tw:cursor-pointer tw:hover:bg-[#523D33]/90"
            : "tw:bg-gray-200 tw:text-gray-400 tw:cursor-not-allowed",
        )}
      >
        <ArrowUp className="tw:size-4" />
      </button>
    </div>
  );
}
